// FILE: /src/Components/Calender.js (FULLY UPDATED)
import React, { useEffect, useState, useMemo, useCallback } from "react";
import moment from "moment";
import momentTimezone from 'moment-timezone';
import { useNavigate } from "react-router-dom";
import { Checkbox, Spin, Button, Tooltip, Switch, Dropdown, Modal, message, Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
EyeOutlined,
CopyOutlined,
DeleteOutlined,
ExclamationCircleOutlined
} from '@ant-design/icons';
import { fetchCalendarPosts, setCalendarFilters, setDateRange } from "../store/Slices/CalendarFilterSlice";
import { fetchGroupDetails } from "../store/Slices/GroupSlice";
import { renderPlatformIcon, renderProfileImage, renderProfileName } from "./ShowProfiles";
import ConnectProfileModal from "./ConnectProfileModal";
import { getOnboardingForGroup, deleteScheduledPost } from "../services/group";
import CreateLinkModal from './Publishing/CreateLinkModal';
import { postApi } from "../services/publish";
import axiosServices from "../services/axios";

const styles = {
  calendarContainer: `flex flex-col h-screen bg-gray-50 p-4 sm:p-6`,
  header: `flex flex-wrap items-center justify-between mb-4 gap-4 flex-shrink-0`,
  headerNav: `flex items-center gap-2`,
  headerNavButton: `p-2 rounded hover:bg-gray-200 transition-colors`,
  headerTodayButton: `px-4 py-2 bg-white text-gray-800 font-semibold rounded-md border hover:bg-gray-100 shadow-sm`,
  headerControls: `flex items-center gap-4`,
  viewModeToggle: `flex rounded-md border bg-white p-1 shadow-sm`,
  viewModeButton: (isActive) => `px-3 py-1 text-sm rounded transition-colors ${isActive ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`,
  filterBar: `flex flex-wrap items-center gap-4 p-3 mb-4 bg-white rounded-lg border border-gray-200 shadow-sm flex-shrink-0`,
  filterDropdownButton: `px-4 py-2 flex items-center gap-2 border rounded-md hover:border-blue-500 bg-white shadow-sm w-full text-left sm:w-auto`,
  calendarBody: `flex flex-col flex-grow bg-white rounded-lg shadow-inner border overflow-hidden relative min-h-0`,
  loadingOverlay: `absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10`,
  emptyState: `flex justify-center items-center h-full text-center text-gray-500`,

  monthViewContainer: `flex flex-col flex-grow`,
  monthGridHeader: `grid grid-cols-7`,
  monthWeekday: `p-2 text-sm font-semibold text-gray-600 text-center border-b-2 border-gray-200 bg-gray-50`,
  monthGrid: `grid grid-cols-7 grid-rows-6 flex-grow`,
  monthDayCell: (isToday, isPast) => `relative p-2 border-t border-l border-gray-200 flex flex-col ${isToday ? 'bg-blue-100' : (isPast ? 'bg-slate-50' : 'bg-white hover:bg-gray-50')}`,
  monthDayNumber: (isToday, isOtherMonth) => `text-sm mb-1 ${isToday ? 'bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center font-bold' : 'font-semibold'} ${isOtherMonth ? 'text-gray-400' : 'text-gray-700'}`,
  monthPostIcons: `flex-grow flex flex-wrap gap-1 content-start`,
  monthAddButton: `absolute bottom-2 right-2 w-6 h-6 flex items-center justify-center bg-gray-200 text-gray-600 rounded-full hover:bg-blue-500 hover:text-white transition-colors`,

  weekViewContainer: `flex flex-col h-full min-h-0`,
weekGridHeader: `grid grid-cols-7 flex-shrink-0`,
weekGrid: `grid grid-cols-7 flex-grow min-h-0 overflow-hidden`,
weekHeaderCell: (isToday) => `p-2 text-sm font-semibold text-center border-b-2 bg-gray-50 border-l ${isToday ? 'text-blue-600' : 'text-gray-600'}`,
weekDayCell: (isPast, isToday) => `p-2 border-l border-t align-top h-full min-h-0 flex flex-col overflow-hidden ${isToday ? 'bg-blue-50' : (isPast ? 'bg-gray-100' : '')}`,
weekDayScrollContainer: `flex-1 min-h-0 overflow-y-auto overflow-x-hidden space-y-2 pr-1 scrollbar-thin`,
weekAddButton: `w-full mt-2 pt-2 text-xs text-center text-gray-500 bg-transparent rounded-md border border-dashed hover:bg-blue-50 hover:text-blue-600 hover:border-blue-400 transition flex-shrink-0`,
};

const CalendarHeader = ({ currentDate, viewMode, setViewMode, onPrev, onNext, onToday, onShareClick, onDateChange }) => {
  const currentYear = currentDate.year();
  const yearOptions = Array.from({ length: 21 }, (_, i) => moment().year() - 10 + i);
  const monthOptions = moment.months();

  const getWeekOptions = (year) => {
    const weeks = [];
    const startOfYear = moment().year(year).startOf('year');
    const endOfYear = moment().year(year).endOf('year');
    let currentWeek = startOfYear.clone().startOf('week');

    while (currentWeek.isBefore(endOfYear)) {
      const startOfWeek = currentWeek.clone();
      const endOfWeek = currentWeek.clone().endOf('week');
      let label;

      if (startOfWeek.month() === endOfWeek.month()) {
        label = `${startOfWeek.format('MMM D')} - ${endOfWeek.format('D')}`;
      } else {
        label = `${startOfWeek.format('MMM D')} - ${endOfWeek.format('MMM D')}`;
      }

      weeks.push({
        value: startOfWeek.week(),
        label
      });

      currentWeek.add(1, 'week');
    }

    return weeks;
  };

  const weekOptions = useMemo(() => getWeekOptions(currentYear), [currentYear]);

  const handleMonthChange = (monthIndex) => {
    onDateChange(currentDate.clone().month(monthIndex));
  };

  const handleYearChange = (year) => {
    onDateChange(currentDate.clone().year(year));
  };

  const handleWeekChange = (weekNumber) => {
    onDateChange(currentDate.clone().week(weekNumber));
  };

  return (
    <div className={styles.header}>
      <div className={styles.headerNav}>
        <button onClick={onPrev} className={styles.headerNavButton}><i className="fa fa-arrow-left" /></button>
        <button onClick={onToday} className={styles.headerTodayButton}>Today</button>
        <button onClick={onNext} className={styles.headerNavButton}><i className="fa fa-arrow-right" /></button>

        <div className="flex items-center gap-2 ml-4">
          {viewMode === 'Month' && (
            <Select value={currentDate.month()} onChange={handleMonthChange} style={{ width: 140 }}>
              {monthOptions.map((month, index) => (
                <Select.Option key={index} value={index}>{month}</Select.Option>
              ))}
            </Select>
          )}

          {viewMode === 'Week' && (
            <Select value={currentDate.week()} onChange={handleWeekChange} style={{ width: 180 }}>
              {weekOptions.map(week => (
                <Select.Option key={week.value} value={week.value}>{week.label}</Select.Option>
              ))}
            </Select>
          )}

          <Select value={currentDate.year()} onChange={handleYearChange} style={{ width: 100 }}>
            {yearOptions.map(year => (
              <Select.Option key={year} value={year}>{year}</Select.Option>
            ))}
          </Select>
        </div>
      </div>

      <div className={styles.headerControls}>
        <Button icon={<i className="fa-solid fa-share-nodes mr-2"></i>} onClick={onShareClick}>Share</Button>
        <div className={styles.viewModeToggle}>
          <button onClick={() => setViewMode('Week')} className={styles.viewModeButton(viewMode === 'Week')}>Week</button>
          <button onClick={() => setViewMode('Month')} className={styles.viewModeButton(viewMode === 'Month')}>Month</button>
        </div>
      </div>
    </div>
  );
};

const FilterDropdown = ({ title, selectedCount = 0, isOpen, onToggle, children, dropdownRef }) => (
  <div className="relative" ref={dropdownRef}>
    <button onClick={onToggle} className={styles.filterDropdownButton}>
      <span className="text-sm font-medium text-gray-700">{title}</span>
      {selectedCount > 0 && (
        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
          {selectedCount}
        </span>
      )}
      <i className={`fa fa-chevron-down text-xs text-gray-500 transition-transform ml-auto ${isOpen && 'rotate-180'}`}></i>
    </button>

    {isOpen && (
      <div className="absolute z-20 top-full mt-1 w-72 p-4 bg-white rounded-lg shadow-xl border animate-fade-in-up">
        <div className="space-y-3 max-h-60 overflow-y-auto pr-2">{children}</div>
      </div>
    )}
  </div>
);

const MonthView = ({ postsByDate, dataObject, navigate, filters, brandTimezone }) => {
  const startOfGrid = dataObject.clone().startOf('month').startOf('week');
  const allDaysInGrid = [];
  let currentDay = startOfGrid.clone();

  for (let i = 0; i < 42; i++) {
    allDaysInGrid.push(currentDay.clone());
    currentDay.add(1, 'day');
  }

  const weekdays = moment.weekdaysShort().map(day => (
    <div key={day} className={styles.monthWeekday}>{day}</div>
  ));

  return (
    <div className={styles.monthViewContainer}>
      <div className={styles.monthGridHeader}>{weekdays}</div>
      <div className={styles.monthGrid}>
        {allDaysInGrid.map(dayMoment => {
          const dateStr = dayMoment.format('YYYY-MM-DD');
          const postsForDay = postsByDate[dateStr] || [];
          const now = brandTimezone ? moment().tz(brandTimezone) : moment();
          const isToday = dayMoment.isSame(now, 'day');
          const isPast = dayMoment.isBefore(now, 'day');
          const isOtherMonth = dayMoment.month() !== dataObject.month();

          const postsByPlatform = postsForDay.reduce((acc, post) => {
            if (post.platform) {
              if (!acc[post.platform]) acc[post.platform] = 0;
              acc[post.platform]++;
            }
            return acc;
          }, {});

          return (
            <div key={dateStr} className={styles.monthDayCell(isToday, isPast)}>
              <div className={styles.monthDayNumber(isToday, isOtherMonth)}>{dayMoment.date()}</div>

              <div className={styles.monthPostIcons}>
                {Object.entries(postsByPlatform).map(([platform, count]) => (
                  <div key={platform} className="flex items-center gap-1 bg-white border px-1.5 py-0.5 rounded-full text-xs shadow-sm">
                    {renderPlatformIcon(platform)}
                    <span className="font-bold">{count}</span>
                  </div>
                ))}
              </div>

              {!isPast && !isOtherMonth && (
                <button
                  onClick={() =>
                    navigate('/Dashboard/PublishPost', {
                      state: {
                        date: dayMoment.date(),
                        month: dayMoment.month(),
                        selectedProfiles: filters.profiles
                      }
                    })
                  }
                  className={styles.monthAddButton}
                  title="Add Post"
                >
                  <i className="fa fa-plus text-xs"></i>
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const StatusTag = ({ status, failureReason }) => {
  switch (status) {
    case 'Scheduled':
      return <span className="text-xs font-semibold bg-blue-100 text-blue-800 px-2 py-1 rounded-full">SCHEDULED</span>;
    case 'Drafts':
      return <span className="text-xs font-semibold bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">DRAFT</span>;
    case 'Processing':
return (
<Tooltip title={failureReason || "This post is still processing on the platform."}>
<span className="text-xs font-semibold bg-cyan-100 text-cyan-800 px-2 py-1 rounded-full cursor-help">
PROCESSING
</span>
</Tooltip>
);
case 'Failed':
return (
<Tooltip title={failureReason || "The post failed to publish. Please check the content and try again."}>
<span className="text-xs font-semibold bg-red-100 text-red-800 px-2 py-1 rounded-full cursor-help">
<ExclamationCircleOutlined className="mr-1" /> FAILED
</span>
</Tooltip>
);
default:
return <span className="text-xs font-semibold bg-green-100 text-green-800 px-2 py-1 rounded-full">PUBLISHED</span>;
  }
};

const PostCard = React.memo(({ post, onView, onDuplicate, onDelete }) => {
return (
<div className="w-full text-left p-2 bg-white rounded-md border border-gray-200 transition-all shadow-sm hover:border-blue-400 hover:shadow-md">
  <div className="flex items-start gap-2">
    {post.mediaType === 'video' ? (
      post.thumbnailUrl ? (
        <div className="relative w-16 h-16 rounded flex-shrink-0 overflow-hidden bg-red-200 border border-red-500">
          <img
            src={post.thumbnailUrl}
            alt="video thumbnail"
            className="w-full h-full object-cover rounded"
            onError={() => {}}
          />
          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-[9px] text-center">
            VIDEO
          </div>
        </div>
      ) : (
        <div className="w-16 h-16 rounded bg-black flex-shrink-0 flex items-center justify-center text-white text-[10px]">
          NO THUMB
        </div>
      )
    ) : post.mediaUrl ? (
      <img src={post.mediaUrl} alt="post" className="w-10 h-10 rounded object-cover flex-shrink-0" />
    ) : (
      <div className="w-10 h-10 rounded bg-gray-200 flex-shrink-0 flex items-center justify-center">
        <i className="fa-solid fa-align-left text-gray-400"></i>
      </div>
    )}

    <div className="flex-grow overflow-hidden">
      <div className="flex items-center gap-1.5 text-xs">
        {renderPlatformIcon(post.platform)}
        <span className="font-semibold truncate">{post.profile?.name || 'Profile'}</span>
      </div>
      <p className="text-xs text-gray-600 truncate">{post.message || "No caption"}</p>
    </div>
  </div>

  <div className="flex justify-between items-center mt-2">
    <StatusTag status={post.status} failureReason={post.originalPost.failureReason} />
    <span className="text-xs font-bold">{moment(post.date).format('h:mm A')}</span>
  </div>

  <div className="flex items-center justify-end gap-2 mt-2 pt-2 border-t border-gray-100">
  <Tooltip title="View">
    <Button
      type="text"
      size="small"
      shape="circle"
      icon={<EyeOutlined />}
      onClick={() => onView(post.originalPost)}
    />
  </Tooltip>

  <Tooltip title="Duplicate">
    <Button
      type="text"
      size="small"
      shape="circle"
      icon={<CopyOutlined />}
      onClick={() => onDuplicate(post.originalPost)}
    />
  </Tooltip>

  {post.originalPost.platform !== 'TIKTOK' && (
    <Tooltip title="Delete">
      <Button
        type="text"
        size="small"
        shape="circle"
        danger
        icon={<DeleteOutlined />}
        onClick={() => onDelete(post.originalPost)}
      />
    </Tooltip>
  )}
</div>
</div>
);
});

const WeekView = ({ postsByDate, dataObject, navigate, filters, brandTimezone, onView, onDuplicate, onDelete }) => {  const startOfWeek = dataObject.clone().startOf('week');
  const days = Array.from({ length: 7 }, (_, i) => startOfWeek.clone().add(i, 'days'));

  return (
    <div className={styles.weekViewContainer}>
      <div className={styles.weekGridHeader}>
        {days.map(day => {
          const now = brandTimezone ? moment().tz(brandTimezone) : moment();
          const isToday = day.isSame(now, 'day');

          return (
            <div key={day.format('YYYY-MM-DD')} className={styles.weekHeaderCell(isToday)}>
              {day.format('ddd')} <span className="text-lg ml-1">{day.format('D')}</span>
            </div>
          );
        })}
      </div>

      <div className={styles.weekGrid}>
        {days.map(day => {
          const postsForDay = postsByDate[day.format('YYYY-MM-DD')] || [];
          const now = brandTimezone ? moment().tz(brandTimezone) : moment();
          const isPast = day.isBefore(now, 'day');
          const isToday = day.isSame(now, 'day');

          return (
            <div key={day.format('YYYY-MM-DD')} className={styles.weekDayCell(isPast, isToday)}>
              <div className={styles.weekDayScrollContainer}>
                {postsForDay.map((post, index) => (
                  <PostCard
                    key={post._id || index}
                    post={post}
                    onView={onView}
                    onDuplicate={onDuplicate}
                    onDelete={onDelete}
                    />
                ))}
              </div>

              {!isPast && (
                <button
                  onClick={() =>
                    navigate('/Dashboard/PublishPost', {
                      state: {
                        date: day.date(),
                        month: day.month(),
                        selectedProfiles: filters.profiles,
                      },
                    })
                  }
                  className={styles.weekAddButton}
                >
                  <i className="fa fa-plus mr-1"></i> Publish Post
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CalendarFilters = ({
  filters,
  availableProfiles,
  profilesLoading,
  currentUser,
  onFilterChange,
  onProfileSelect,
  onPlatformHeaderClick,
  onTimezoneToggle,
  displayTimezone,
  brandTimezone
}) => {
  const [openDropdown, setOpenDropdown] = useState(null);

  const filterDropdownRefs = useMemo(() => ({
    platforms: React.createRef(),
    profiles: React.createRef(),
    author: React.createRef(),
    status: React.createRef(),
  }), []);

  const dynamicPlatformItems = useMemo(() => {
    if (!availableProfiles || availableProfiles.length === 0) return [];
    const uniquePlatforms = new Set(availableProfiles.map(p => p.platform));
    return Array.from(uniquePlatforms);
  }, [availableProfiles]);

  const profilesByPlatform = useMemo(() => {
    if (!availableProfiles || availableProfiles.length === 0) return {};
    return [...availableProfiles].reduce((acc, p) => {
      if (!acc[p.platform]) acc[p.platform] = [];
      acc[p.platform].push(p);
      return acc;
    }, {});
  }, [availableProfiles]);

  const handleToggleDropdown = useCallback((name) => {
    setOpenDropdown(prev => (prev === name ? null : name));
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !Object.values(filterDropdownRefs).some(ref => ref.current?.contains(event.target))) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdown, filterDropdownRefs]);

  const userTimezone = momentTimezone.tz.guess();

  return (
    <div className={`${styles.filterBar} justify-between`}>
      <div className="flex flex-wrap items-center gap-4">
        <FilterDropdown
          title="Platforms"
          selectedCount={filters.platforms.length}
          isOpen={openDropdown === 'platforms'}
          onToggle={() => handleToggleDropdown('platforms')}
          dropdownRef={filterDropdownRefs.platforms}
        >
          {dynamicPlatformItems.map(p => (
            <Checkbox key={p} checked={filters.platforms.includes(p)} onChange={() => onFilterChange('platforms', p, true)}>
              <div className="flex items-center gap-2">
                {renderPlatformIcon(p)}
                <span>{p.charAt(0) + p.slice(1).toLowerCase()}</span>
              </div>
            </Checkbox>
          ))}
        </FilterDropdown>

        <FilterDropdown
          title="Profiles"
          selectedCount={filters.profiles.length}
          isOpen={openDropdown === 'profiles'}
          onToggle={() => handleToggleDropdown('profiles')}
          dropdownRef={filterDropdownRefs.profiles}
        >
          {profilesLoading ? (
            <Spin />
          ) : Object.keys(profilesByPlatform).length > 0 ? (
            Object.entries(profilesByPlatform).map(([platform, profiles]) => {
              const profileIdsForPlatform = profiles.map(p => p.platform === 'FACEBOOK' ? p.platform_pageId : p.platform_id);
              const allSelected = profileIdsForPlatform.every(id => filters.profiles.includes(id));
              const someSelected = !allSelected && profileIdsForPlatform.some(id => filters.profiles.includes(id));

              return (
                <div key={platform}>
                  <div className="flex items-center mt-2 mb-1 border-b pb-1">
                    <Checkbox checked={allSelected} indeterminate={someSelected} onChange={() => onPlatformHeaderClick(profiles)} />
                    <h4 className="font-semibold text-gray-600 text-sm ml-2 flex items-center gap-2">
                      {renderPlatformIcon(platform)}
                      {platform.charAt(0) + platform.slice(1).toLowerCase()}
                    </h4>
                  </div>

                  <div className="space-y-2 pl-1">
                    {profiles.map(p => {
                      const uniqueKey = p.platform === 'FACEBOOK' ? p.platform_pageId : p.platform_id;
                      return (
                        <Checkbox key={uniqueKey} checked={filters.profiles.includes(uniqueKey)} onChange={() => onProfileSelect(uniqueKey)}>
                          <div className="flex items-center gap-2">
                            {renderProfileImage(p)}
                            {renderProfileName(p)}
                          </div>
                        </Checkbox>
                      );
                    })}
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-sm text-gray-500">No connected profiles found for this group.</p>
          )}
        </FilterDropdown>

        <FilterDropdown
          title="Status"
          selectedCount={filters.statuses.length}
          isOpen={openDropdown === 'status'}
          onToggle={() => handleToggleDropdown('status')}
          dropdownRef={filterDropdownRefs.status}
        >
          {["Published", "Scheduled", "Drafts", "Failed", "Processing"].map(s => (
<Checkbox key={s} checked={filters.statuses.includes(s)} onChange={() => onFilterChange('statuses', s, true)}>
{s}
</Checkbox>
))}
        </FilterDropdown>

        <FilterDropdown
          title="Author"
          selectedCount={filters.authorId ? 1 : 0}
          isOpen={openDropdown === 'author'}
          onToggle={() => handleToggleDropdown('author')}
          dropdownRef={filterDropdownRefs.author}
        >
          <Checkbox checked={!!filters.authorId} onChange={() => onFilterChange('authorId', filters.authorId ? null : currentUser._id)}>
            {currentUser?.first_name || 'Me'}
          </Checkbox>
        </FilterDropdown>
      </div>

      {brandTimezone && brandTimezone !== userTimezone && (
        <div className="flex items-center gap-2 text-sm">
          <Tooltip title={`Your local time: ${userTimezone.replace(/_/g, " ")}`}>
            <span className={displayTimezone === 'user' ? 'font-bold text-blue-600' : 'text-gray-500'}>My Time</span>
          </Tooltip>
          <Switch
            checked={displayTimezone === 'brand'}
            onChange={(checked) => onTimezoneToggle(checked ? 'brand' : 'user')}
          />
          <Tooltip title={`Brand's local time: ${brandTimezone.replace(/_/g, " ")}`}>
            <span className={displayTimezone === 'brand' ? 'font-bold text-blue-600' : 'text-gray-500'}>Brand Time</span>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export default function Calender() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState("Month");
  const [currentDate, setCurrentDate] = useState(moment());
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
  const [brandTimezone, setBrandTimezone] = useState(null);
  const [displayTimezone, setDisplayTimezone] = useState('user');
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const { posts, status: globalStatus, filters } = useSelector((state) => state.calendar);
  const { selectedGroup, groups, profile: availableProfiles, loading: profilesLoading } = useSelector((state) => state.groups);
  const currentUser = useSelector((state) => state.Profiles[0]);

  const selectedGroupId = useMemo(() => groups.find(g => g.name === selectedGroup)?._id, [selectedGroup, groups]);

  useEffect(() => {
    if (availableProfiles) {
      const uniquePlatforms = Array.from(new Set(availableProfiles.map(p => p.platform)));
      dispatch(setCalendarFilters({ platforms: uniquePlatforms }));
    }
  }, [availableProfiles, dispatch]);

  useEffect(() => {
    const fetchBrandData = async () => {
      if (selectedGroupId) {
        dispatch(fetchGroupDetails(selectedGroup));
        try {
          const onboardingData = await getOnboardingForGroup(selectedGroupId);
          setBrandTimezone(onboardingData?.time_zone || null);
        } catch (error) {
          console.warn("Could not fetch brand timezone for calendar.", error);
          setBrandTimezone(null);
        }
      }
    };
    fetchBrandData();
  }, [selectedGroup, selectedGroupId, dispatch]);

  const postsByDate = useMemo(() => {
    if (!posts || posts.length === 0) return {};

    return posts.reduce((acc, post) => {
      const postDateUTC = post.status === 'Published' ? post.date : post.scheduledTime;
      if (!postDateUTC || !moment(postDateUTC).isValid()) return acc;

      const targetTimezone = displayTimezone === 'brand' && brandTimezone ? brandTimezone : momentTimezone.tz.guess();
      const dateKey = moment.tz(postDateUTC, targetTimezone).format('YYYY-MM-DD');

      if (!acc[dateKey]) acc[dateKey] = [];

      const imageUrl =
        post.pinterest_thumbnail_url ||
        post.image_url?.[0] ||
        post.media_url?.[0] ||
        post.source_image_url?.[0] ||
        null;

      const videoUrl =
        post.video_url?.[0] ||
        post.source_video_url?.[0] ||
        null;

      const mediaType =
        post.media_type ||
        (videoUrl ? 'video' : imageUrl ? 'image' : 'text');

      const mediaUrl = mediaType === 'video' ? videoUrl : imageUrl;
      const thumbnailUrl = imageUrl;

      const commonData = {
        _id: post._id,
        status: post.status,
        date: postDateUTC,
        message: post.message || 'No Caption',
        mediaUrl,
        thumbnailUrl,
        mediaType,
        originalPost: post,
        };

        

      if (post.status === 'Published') {
        const profileIdToFind = post.platform === 'PINTEREST' ? post.user_id : post.profileId;
        const profileInfo = availableProfiles?.find(
          p => (p.platform_pageId === profileIdToFind || p.platform_id === profileIdToFind)
        );

        if (profileInfo) {
          acc[dateKey].push({
            ...commonData,
            platform: post.platform,
            profile: { name: profileInfo?.profile || profileInfo?.username || 'Profile', ...profileInfo }
          });
        }
      } else {
        const profileIds = post.selectedProfiles || [];
        profileIds.forEach(profileId => {
          const profileInfo = availableProfiles?.find(
            p => (p.platform_pageId === profileId || p.platform_id === profileId)
          );

          if (profileInfo) {
            acc[dateKey].push({
              ...commonData,
              _id: `${post._id}-${profileInfo.platform}-${profileId}`,
              platform: profileInfo.platform,
              profile: { name: profileInfo?.profile || profileInfo?.username || 'Profile', ...profileInfo }
            });
          }
        });
      }

      return acc;
    }, {});
  }, [posts, availableProfiles, displayTimezone, brandTimezone]);

  useEffect(() => {
    const start = moment(currentDate).startOf(viewMode.toLowerCase());
    const end = moment(currentDate).endOf(viewMode.toLowerCase());
    dispatch(setDateRange({ start: start.toISOString(), end: end.toISOString() }));
  }, [viewMode, currentDate, dispatch]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (selectedGroupId && filters.profiles) {
        dispatch(fetchCalendarPosts({ ...filters, groupId: selectedGroupId }));
      }
    }, 300);

    return () => clearTimeout(handler);
  }, [filters, selectedGroupId, dispatch]);

  const handleFilterChange = (key, value, isCheckboxToggle = false) => {
    if (isCheckboxToggle) {
      const currentValues = filters[key] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      dispatch(setCalendarFilters({ [key]: newValues }));
    } else {
      dispatch(setCalendarFilters({ [key]: value }));
    }
  };

  const handleProfileSelection = (profileId) => handleFilterChange('profiles', profileId, true);

  const handlePlatformHeaderClick = (platformProfiles) => {
    const profileIdsForPlatform = platformProfiles.map(p =>
      p.platform === 'FACEBOOK' ? p.platform_pageId : p.platform_id
    );

    const currentSelected = filters.profiles || [];
    const allSelected = profileIdsForPlatform.every(id => currentSelected.includes(id));

    const newSelectedProfiles = allSelected
      ? currentSelected.filter(id => !profileIdsForPlatform.includes(id))
      : [...new Set([...currentSelected, ...profileIdsForPlatform])];

    dispatch(setCalendarFilters({ profiles: newSelectedProfiles }));
  };

  const handleView = (post) => {
    if (post.platform_url) {
      window.open(post.platform_url, '_blank', 'noopener,noreferrer');
      return;
    }
    message.warning("This post does not have a platform link yet.");
  };

  const handleDuplicate = async (post) => {
    const mediaUrls = [
      ...(Array.isArray(post.media_url) ? post.media_url : []),
      ...(Array.isArray(post.image_url) ? post.image_url : []),
      ...(Array.isArray(post.video_url) ? post.video_url : [])
    ];

    const uniqueMediaUrls = [...new Set(mediaUrls.filter(Boolean))];

    const mediaItems = uniqueMediaUrls.map(url => ({
      url,
      type: /\.(mp4|mov|webm)$/i.test(url) ? 'video' : 'image'
    }));

    const selectedProfileIds = post.selectedProfiles && post.selectedProfiles.length > 0
      ? post.selectedProfiles
      : (post.profileId ? [post.profileId] : []);

    const platforms = post.platforms && post.platforms.length > 0
      ? post.platforms
      : (post.platform ? [post.platform] : []);

    const payload = {
      groupId: post.groupId,
      groupName: post.groupName,
      media_items: mediaItems,
      media_type: post.media_type || (mediaItems[0]?.type || 'text'),
      selectedProfiles: selectedProfileIds,
      platforms,
      message: post.message || '',
      first_comment: post.first_comment || '',
      platformOptions: post.platformOptions || {},
      status: 'draft',
      scheduledTime: null
    };

    try {
      await postApi(payload);
      message.success("Post duplicated as a new draft.");
      dispatch(fetchCalendarPosts({ ...filters, groupId: selectedGroupId }));
    } catch (error) {
      message.error(error.message || "Failed to duplicate post.");
    }
  };

  const handleDelete = (post) => {
if (post.status === 'Processing') {
message.warning("This post is still processing on the platform and cannot be deleted yet.");
return;
}

Modal.confirm({
title: 'Delete this post?',
content: 'This action cannot be undone.',
okText: 'Delete',
okType: 'danger',
onOk: async () => {
try {
if (post.status === 'Published') {
await axiosServices.delete(`/posts/published/${post._id}`);
} else {
await deleteScheduledPost(post._id);
}
message.success("Post deleted.");
dispatch(fetchCalendarPosts({ ...filters, groupId: selectedGroupId }));
} catch (error) {
message.error(error?.response?.data?.message || "Failed to delete post.");
}
},
});
};

  const renderCalendarContent = () => {
    const isLoading = globalStatus === 'loading';
    const isFetchSuccess = globalStatus === 'succeeded';

    if (isLoading) {
      return <div className={styles.loadingOverlay}><Spin size="large" /></div>;
    }

    const hasVisiblePosts = Object.keys(postsByDate).length > 0;
    const hasPostsInState = posts.length > 0;

    if (isFetchSuccess && !hasPostsInState) {
      return (
        <div className={styles.emptyState}>
          <div>
            <i className="fa fa-calendar-times text-4xl mb-4 text-gray-400"></i>
            <h2 className="text-xl font-semibold">No Posts Found</h2>
            <p className="mb-4">Try adjusting your filters or connect a profile to get started.</p>
            <div className="flex justify-center gap-4">
              <Button type="default" onClick={() => setIsConnectModalOpen(true)}>Connect Profile</Button>
              <Button type="primary" onClick={() => navigate('/Dashboard/PublishPost')}>Publish a Post</Button>
            </div>
          </div>
        </div>
      );
    }

    if (isFetchSuccess && hasPostsInState && !hasVisiblePosts) {
      return (
        <div className={styles.emptyState}>
          <div>
            <i className="fa-solid fa-box-archive text-4xl mb-4 text-gray-400"></i>
            <h2 className="text-xl font-semibold">Unscheduled Drafts Found</h2>
            <p className="mb-4">Your drafts with no scheduled date won't appear on the calendar.</p>
            <div className="flex justify-center gap-4">
              <Button type="default" onClick={() => navigate('/Dashboard/QueuePage')}>View in Queue</Button>
              <Button type="primary" onClick={() => navigate('/Dashboard/PublishPost')}>Publish a Post</Button>
            </div>
          </div>
        </div>
      );
    }

    if (hasVisiblePosts) {
      return viewMode === 'Month'
        ? (
          <MonthView
            postsByDate={postsByDate}
            dataObject={currentDate}
            navigate={navigate}
            filters={filters}
            brandTimezone={brandTimezone}
          />
        )
        : (
          <WeekView
postsByDate={postsByDate}
dataObject={currentDate}
navigate={navigate}
filters={filters}
brandTimezone={brandTimezone}
onView={handleView}
onDuplicate={handleDuplicate}
onDelete={handleDelete}
/>
        );
    }

    return null;
  };

  if (!selectedGroup) {
    return (
      <div className="flex justify-center items-center h-screen text-center text-gray-500">
        <div>
          <i className="fa fa-folder-open text-4xl mb-4"></i>
          <h2 className="text-xl font-semibold">No Group Selected</h2>
          <p>Please select a group from the sidebar to view its calendar.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.calendarContainer}>
      <CalendarHeader
        currentDate={currentDate}
        viewMode={viewMode}
        setViewMode={setViewMode}
        onPrev={() => setCurrentDate(currentDate.clone().subtract(1, viewMode.toLowerCase() === 'week' ? 'weeks' : 'months'))}
        onNext={() => setCurrentDate(currentDate.clone().add(1, viewMode.toLowerCase() === 'week' ? 'weeks' : 'months'))}
        onToday={() => setCurrentDate(moment())}
        onShareClick={() => setIsShareModalOpen(true)}
        onDateChange={(date) => setCurrentDate(date)}
      />

      <CalendarFilters
        filters={filters}
        availableProfiles={availableProfiles}
        profilesLoading={profilesLoading}
        currentUser={currentUser}
        onFilterChange={handleFilterChange}
        onProfileSelect={handleProfileSelection}
        onPlatformHeaderClick={handlePlatformHeaderClick}
        onTimezoneToggle={setDisplayTimezone}
        displayTimezone={displayTimezone}
        brandTimezone={brandTimezone}
      />

      <div className={styles.calendarBody}>
        {renderCalendarContent()}
      </div>

      <ConnectProfileModal
        isOpen={isConnectModalOpen}
        onClose={() => setIsConnectModalOpen(false)}
      />

      <CreateLinkModal
isOpen={isShareModalOpen}
onClose={() => setIsShareModalOpen(false)}
posts={posts.filter(p => p.status === 'scheduled' || p.status === 'draft')}
/>
    </div>
  );
}